import { useCallback, useRef, useState } from "react";

/**
 * An element's rendered border-box width, kept current by a `ResizeObserver`. Attach the returned
 * callback ref to the element; `width` is `undefined` until it has been measured, after it
 * detaches, and wherever `ResizeObserver` is unavailable — so a caller can tell "not measured"
 * apart from a real 0 and fall back to its own default.
 *
 * The FIRST measurement is taken synchronously inside the callback ref, which runs in the commit
 * phase: a state update there is flushed before the browser paints, so a layout decided by this
 * width never shows a frame of its pre-measurement form. The observer only reports later changes.
 * It is disconnected whenever the ref detaches, which covers both unmount and a swap to a
 * different node.
 */
export function useElementWidth<T extends HTMLElement>(): [
  ref: (node: T | null) => void,
  width: number | undefined,
] {
  const [width, setWidth] = useState<number | undefined>(undefined);
  const observerRef = useRef<ResizeObserver | null>(null);

  const ref = useCallback((node: T | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;

    if (node === null || typeof ResizeObserver === "undefined") {
      setWidth(undefined);
      return;
    }

    setWidth(node.getBoundingClientRect().width);
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // Border-box to match `getBoundingClientRect` above; `contentRect` only where the browser
        // predates `borderBoxSize`.
        setWidth(entry.borderBoxSize?.[0]?.inlineSize ?? entry.contentRect.width);
      }
    });
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  return [ref, width];
}
